import i18n from '@link/portal/i18n';
import { COUNTRY_NAMES } from '@link/portal/i18n/country';
import { Locale } from '@link/portal/services/types';
import { Country, Language, LanguageV2 } from '@link/portal/services/const';

export const LOCALE = {
  EN: 'en',
  JA: 'ja',
  KO: 'ko',
  ZH: 'zh',
  ZT: 'zt'
} as const;
export type LOCALE = (typeof LOCALE)[keyof typeof LOCALE];

const LANGUAGE_MAP: Partial<Record<LOCALE, Language>> = {
  [LOCALE.EN]: Language.English,
  [LOCALE.JA]: Language.Japanese,
  [LOCALE.KO]: Language.Korean,
  [LOCALE.ZH]: Language.Chinese,
  [LOCALE.ZT]: Language.ChineseT
};

export const REVERSE_LANGUAGE_MAP = Object.entries(LANGUAGE_MAP).reduce(
  (acc, [key, value]) => {
    acc[value] = key as LOCALE;
    return acc;
  },
  {} as Partial<Record<Language, LOCALE>>
);

export const DEFAULT_LANGUAGE = Language.Japanese;

export const DEFAULT_LOCALE = REVERSE_LANGUAGE_MAP[DEFAULT_LANGUAGE];

export const LOCALE_LIST = Object.values(LOCALE);

export function getCurrentLocale(currentLang?: string): Locale {
  const locale = (currentLang || i18n.language) as LOCALE;
  const lang = LANGUAGE_MAP[locale] || Language.Japanese;

  return {
    language: lang,
    country: Country.Universal
  };
}

export const LANGUAGE_TO_LOCALE: Partial<Record<LanguageV2, LOCALE>> = {
  [LanguageV2.English]: LOCALE.EN,
  [LanguageV2.Japanese]: LOCALE.JA,
  [LanguageV2.Korean]: LOCALE.KO,
  [LanguageV2.SimplifiedChinese]: LOCALE.ZH,
  [LanguageV2.TraditionalChinese]: LOCALE.ZT
};

/**
 * Convert a CountryCode enum value (e.g. "CHN") into a localized display
 * string based on the activity language.
 *
 * Output format: `localizedName(CODE)`, e.g. `日本(JPN)` / `中華人民共和国(CHN)`.
 * For English, the `(CODE)` suffix is omitted, e.g. `Japan`.
 * Empty / unknown / UNDEFINED / UNIVERSAL codes return an empty string.
 *
 * The language is fixed to the activity language (not the current UI locale),
 * per the booking field display requirement.
 */
export function formatCountryName(
  code?: string | null,
  activityLang?: LanguageV2 | null
): string {
  if (!code || code === 'UNDEFINED' || code === 'UNIVERSAL') return '';

  const locale = LANGUAGE_TO_LOCALE[activityLang as LanguageV2] || LOCALE.EN;
  const name = COUNTRY_NAMES[locale]?.[code];

  if (!name) return code;
  if (locale === LOCALE.EN) return name;
  return `${name}(${code})`;
}
